import { FONTS } from './catalog';

/** Font files shipped in assets/fonts (served to browsers at /fonts/…). */
export interface FontFile {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
  file: string;
  /** CSS unicode-range so browsers only fetch what they need */
  unicodeRange: string;
}

const LATIN = 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
const LATIN_EXT = 'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF';

const AVAILABLE: Record<string, { weights: number[]; italic: boolean; latinExt: boolean }> = {
  Inter: { weights: [400, 700], italic: true, latinExt: true },
  'Roboto Slab': { weights: [400, 700], italic: false, latinExt: true },
  'Playfair Display': { weights: [400, 700], italic: true, latinExt: true },
  'Bebas Neue': { weights: [400], italic: false, latinExt: true },
  'JetBrains Mono': { weights: [400, 700], italic: true, latinExt: true },
  'Permanent Marker': { weights: [400], italic: false, latinExt: false },
  Montserrat: { weights: [400, 700], italic: true, latinExt: true },
  Poppins: { weights: [400, 700], italic: true, latinExt: true },
  'Open Sans': { weights: [400, 700], italic: true, latinExt: true },
  Lato: { weights: [400, 700], italic: true, latinExt: true },
  Raleway: { weights: [400, 700], italic: true, latinExt: true },
  'Space Grotesk': { weights: [400, 700], italic: false, latinExt: true },
  Oswald: { weights: [400, 700], italic: false, latinExt: true },
  Anton: { weights: [400], italic: false, latinExt: true },
  'Archivo Black': { weights: [400], italic: false, latinExt: true },
  'Abril Fatface': { weights: [400], italic: false, latinExt: true },
  Merriweather: { weights: [400, 700], italic: true, latinExt: true },
  Lora: { weights: [400, 700], italic: true, latinExt: true },
  'Dancing Script': { weights: [400, 700], italic: false, latinExt: true },
  Caveat: { weights: [400, 700], italic: false, latinExt: true },
  Pacifico: { weights: [400], italic: false, latinExt: true },
  Lobster: { weights: [400], italic: false, latinExt: true },
  'Press Start 2P': { weights: [400], italic: false, latinExt: true },
};

export const FONT_FILES: FontFile[] = FONTS.flatMap((family) => {
  const a = AVAILABLE[family];
  const slug = family.toLowerCase().replace(/\s+/g, '-');
  const out: FontFile[] = [];
  for (const weight of a.weights) {
    for (const style of a.italic ? (['normal', 'italic'] as const) : (['normal'] as const)) {
      out.push({ family, weight, style, file: `${slug}-latin-${weight}-${style}.woff2`, unicodeRange: LATIN });
      if (a.latinExt) out.push({ family, weight, style, file: `${slug}-latin-ext-${weight}-${style}.woff2`, unicodeRange: LATIN_EXT });
    }
  }
  return out;
});

export function fontWeights(family: string): number[] {
  return AVAILABLE[family]?.weights ?? [400];
}

// ---------------------------------------------------------------------------
// Custom (uploaded) fonts
// ---------------------------------------------------------------------------

/**
 * Uploaded fonts are font assets. Text clips refer to one through a family
 * name derived from its asset id, which the browser and the worker register
 * the file under.
 */
const CUSTOM_PREFIX = 'vmfont-';

export const FONT_EXTENSIONS = ['ttf', 'otf', 'woff', 'woff2'] as const;

/** Largest font file accepted for upload. */
export const MAX_FONT_BYTES = 30 * 1024 ** 2;

export function customFontFamily(assetId: string): string {
  return CUSTOM_PREFIX + assetId;
}

/** The font asset a family name refers to, or null for built-in fonts. */
export function customFontAssetId(family: string): string | null {
  return family.startsWith(CUSTOM_PREFIX) ? family.slice(CUSTOM_PREFIX.length) : null;
}

/** Canonical MIME type for a font file extension, or null if it is not a font. */
export function fontMimeForExt(ext: string): string | null {
  const e = ext.toLowerCase();
  return (FONT_EXTENSIONS as readonly string[]).includes(e) ? `font/${e}` : null;
}
